import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

// Poll the silent /auth/session endpoint at this cadence to update the
// countdown & fire the 5-minute idle warning. Uses `meta.polling` so it does
// NOT extend the session.
const SESSION_POLL_INTERVAL_MS = 60_000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // null while loading
  const [privileges, setPrivileges] = useState({});
  const [ready, setReady] = useState(false);

  const warnFiredRef = useRef(false);
  const pollTimerRef = useRef(null);

  const loadPrivileges = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/role-privileges");
      setPrivileges(data || {});
    } catch (_) { setPrivileges({}); }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadPrivileges();
    } catch (_) {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, [loadPrivileges]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // --- Silent session poll: fires 5-min warning + auto-detects server-side timeout ---
  const pollSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/session", { meta: { polling: true } });
      if (!data) return;
      if (data.expired_reason) {
        window.dispatchEvent(new CustomEvent("session-expired", { detail: { reason: data.expired_reason } }));
        return;
      }
      const warnAt = data.warn_before_seconds || 300;
      if (data.idle_remaining > 0 && data.idle_remaining <= warnAt) {
        if (!warnFiredRef.current) {
          warnFiredRef.current = true;
          window.dispatchEvent(new CustomEvent("session-idle-warning", {
            detail: { idle_remaining: data.idle_remaining },
          }));
        }
      } else if (data.idle_remaining > warnAt) {
        // Reset so the banner can reappear next time we approach the deadline.
        warnFiredRef.current = false;
      }
    } catch (_) {
      // network/session errors are handled by the response interceptor
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    // Kick off immediately + on interval while logged in.
    pollSession();
    pollTimerRef.current = setInterval(pollSession, SESSION_POLL_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [user, pollSession]);

  const heartbeat = useCallback(async () => {
    try {
      const { data } = await api.post("/auth/heartbeat");
      warnFiredRef.current = false;
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  const login = async (username, password) => {
    try {
      const { data } = await api.post("/auth/login", { username, password });
      if (data.access_token) {
        localStorage.setItem("aroma_token", data.access_token);
      }
      setUser(data.user);
      warnFiredRef.current = false;
      await loadPrivileges();
      return { ok: true, user: data.user };
    } catch (e) {
      // Sep 2026 Phase 22z — Bedakan network error vs backend error supaya
      // user tahu penyebab sebenarnya (jangan disamarkan sebagai "Terjadi
      // kesalahan" generik). Ini penting saat deployment issue supaya root
      // cause visible di layar login.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let message;
      if (!e?.response) {
        // Tidak ada response → network error / CORS block / DNS / timeout.
        message = e?.message
          ? `Tidak dapat terhubung ke server (${e.message}). Cek koneksi internet.`
          : "Tidak dapat terhubung ke server. Cek koneksi internet.";
      } else if (detail) {
        message = formatApiErrorDetail(detail);
      } else if (status >= 500) {
        message = `Server error (${status}). Silakan coba beberapa saat lagi atau hubungi administrator.`;
      } else if (status === 404) {
        message = "Endpoint login tidak ditemukan (404). Konfigurasi backend belum benar.";
      } else {
        message = `Gagal login (HTTP ${status || "?"}).`;
      }
      return { ok: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    localStorage.removeItem("aroma_token");
    setUser(false);
    setPrivileges({});
  };

  const hasPriv = (flag) => {
    if (!user || !user.role) return false;
    const rp = privileges[user.role] || {};
    return !!rp[flag];
  };

  const refresh = fetchMe;

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, refresh, setUser, privileges, hasPriv, reloadPrivileges: loadPrivileges, heartbeat }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
